import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { deleteFailedPersona } from "@/lib/delete-failed-persona";
import { personaDeletionMessage } from "@shared/persona-deletion";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Plus, Search, Zap, Shield, Activity, Loader2, AlertCircle, Heart, ChevronDown, Bookmark, SquareArrowOutUpRight, Trash2 } from "lucide-react";
import { Link } from "wouter";
import { useState, useMemo, useEffect, useRef } from "react";
import type { Persona, Campaign, Brand } from "@shared/schema";
import { Skeleton } from "@/components/ui/skeleton";
import { ShareDialog } from "@/components/share-dialog";
import { optimizedImg } from "@/lib/img";
import { PersonaBuildCard, fallbackBuildProgress, personaNameTier, PERSONA_NAME_TIER_COMPACT, type BuildProgress } from "@/components/persona-build-card";
import { computePersonaStrength, isPersonaStrengthReady, PERSONA_STRENGTH_READY_THRESHOLD, type PersonaStrengthInput } from "@shared/persona-strength";
import {
  isPersonaCoverRegenerated,
  isPersonaCoverRegenerating,
  resolvePersonaCardBandImage,
} from "@/lib/persona-cover";

const AWARENESS_STAGES = [
  { value: "unaware", label: "Unaware" },
  { value: "problem_aware", label: "Problem Aware" },
  { value: "solution_aware", label: "Solution Aware" },
  { value: "product_aware", label: "Product Aware" },
  { value: "most_aware", label: "Most Aware" },
];

const AWARENESS_COLORS: Record<string, { dot: string; bg: string }> = {
  unaware: { dot: "bg-red-400", bg: "bg-red-400/15" },
  problem_aware: { dot: "bg-amber-400", bg: "bg-amber-400/15" },
  solution_aware: { dot: "bg-blue-400", bg: "bg-blue-400/15" },
  product_aware: { dot: "bg-violet-400", bg: "bg-violet-400/15" },
  most_aware: { dot: "bg-emerald-400", bg: "bg-emerald-400/15" },
};

function personaStrength(p: Persona): number {
  return computePersonaStrength(p as PersonaStrengthInput);
}
function getInitials(name: string): string {
  return name.split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

// Serif display face for the cinematic card title/quote (the app's --font-serif
// alias resolves to Inter, so we pin a real serif stack here).
const SERIF = { fontFamily: `'Iowan Old Style', 'Palatino Linotype', Georgia, 'Times New Roman', serif` };

// Warm near-black band color from the reference design.
const BAND = "#231c16";

// Every finished persona card renders to one uniform height: the hero band is
// fixed, and the chip / tension bands reserve their space whether or not the
// persona has the data. The generating/failed placeholder and the loading
// skeleton pin to the same floor so the grid never goes ragged. The floor sits
// just under the finished card's natural height so no leftover space pools
// between the quote strip and the footer (grid rows still stretch via h-full,
// keeping footers bottom-aligned when a neighbor expands its Core Tension).
const CARD_MIN_H = "min-h-[480px]";

// Name type classes for the finished card's hero band, keyed by
// personaNameTier: the full name is always shown — longer names step down in
// size and wrap to more lines so they still fit the fixed-height band.
const MAIN_NAME_TIER_CLASS: Record<ReturnType<typeof personaNameTier>, string> = {
  base: "text-[22px] md:text-[24px] leading-[24px] md:leading-[26px]",
  medium: "text-[17px] md:text-[18px] leading-[21px] md:leading-[22px]",
  small: "text-[14px] md:text-[15px] leading-[18px] md:leading-[19px]",
  xs: "text-[12.5px] md:text-[13px] leading-[16px] md:leading-[17px]",
};

// Pulls the first usable line of text out of a persona jsonb field that may
// be a string, an array of strings, or an array of objects with assorted keys.
function firstText(v: unknown, depth = 0): string | null {
  if (v == null || depth > 3) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t || null;
  }
  if (Array.isArray(v)) {
    for (const item of v) {
      const t = firstText(item, depth + 1);
      if (t) return t;
    }
    return null;
  }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    for (const key of ["quote", "text", "value", "trigger", "label", "title", "name"]) {
      const t = firstText(o[key], depth + 1);
      if (t) return t;
    }
    for (const val of Object.values(o)) {
      const t = firstText(val, depth + 1);
      if (t) return t;
    }
  }
  return null;
}

// Compact side-by-side trait chip (reference-card design): icon + label row,
// then ~2 short lines of condensed text. Chips are height-locked: the text
// slot always reserves two lines (explicit line-height x 2) so a one-line and
// a two-line chip occupy identical space and the FEAR/DESIRE/TRIGGER band
// lines up across every card in a row. Missing values render a quiet
// placeholder rather than dropping the chip.
function TraitChip({ label, text, icon: Icon, tint, testId }: {
  label: string;
  text: string | null;
  icon: typeof Shield;
  tint: { icon: string; iconBg: string; label: string };
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border/60 bg-card/60 px-2 py-2.5 min-w-0" data-testid={testId}>
      <div className="flex items-center gap-1 min-w-0">
        <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border ${tint.iconBg}`}>
          <Icon className={`h-3 w-3 ${tint.icon}`} />
        </div>
        <p className={`text-[8.5px] leading-[13px] font-bold uppercase tracking-[0.08em] truncate ${tint.label}`}>{label}</p>
      </div>
      <p className={`text-[11px] leading-[15px] h-[30px] line-clamp-2 ${text ? "text-muted-foreground" : "text-muted-foreground/45 italic"}`}>
        {text || "Not captured yet"}
      </p>
    </div>
  );
}

function StrengthGauge({ pct }: { pct: number }) {
  const C = 2 * Math.PI * 16; // r=16 in a 40x40 viewBox
  return (
    <div className="relative w-[44px] h-[44px] flex-shrink-0" data-testid="gauge-persona-strength">
      <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90">
        <circle cx="20" cy="20" r="16" fill="none" strokeWidth="3.5" className="stroke-muted/70" />
        <circle
          cx="20" cy="20" r="16" fill="none" strokeWidth="3.5" strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * C} ${C}`}
          className={pct >= PERSONA_STRENGTH_READY_THRESHOLD ? "stroke-emerald-500" : "stroke-emerald-600/80"}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold tabular-nums">{pct}</span>
    </div>
  );
}

function StatPill({ label, value, icon: Icon, accent }: { label: string; value: number | string; icon: typeof Users; accent?: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-card/80 border border-border/50 backdrop-blur-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent || "bg-primary/10"}`}>
        <Icon className={`h-4 w-4 ${accent ? "text-white/80" : "text-primary"}`} />
      </div>
      <div>
        <p className="text-lg font-bold tracking-tight leading-none">{value}</p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function PersonaCard({ persona, brandId, campaignCount, index, workspaceId }: {
  persona: Persona;
  brandId: string;
  campaignCount: number;
  index: number;
  workspaceId: string;
}) {
  const [tensionOpen, setTensionOpen] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deletingRef = useRef(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Failed generations can't be opened (the card isn't clickable), so the
  // grid offers a hard delete instead — the server only accepts it for
  // personas whose status is "failed".
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await deleteFailedPersona(persona.id);
    },
    onSuccess: () => {
      setConfirmDelete(false);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/personas`] });
      toast({ title: "Persona deleted", description: "The failed generation was removed." });
    },
    onError: (e: any) => {
      setConfirmDelete(false);
      toast({ title: "Could not delete persona", description: personaDeletionMessage(e), variant: "destructive" });
    },
    onSettled: () => { deletingRef.current = false; },
  });
  const starterWork = (persona as any).starterWork as {
    state: "queued" | "running" | "interrupted" | "failed" | "completed" | "discarded";
    error?: string | null;
  } | undefined;
  const retryStarterMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/personas/${persona.id}/starter-retry`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/personas`] });
      toast({ title: "Starter persona queued", description: "The saved input will be retried without replacing successful personas." });
    },
    onError: (error: any) => {
      toast({ title: "Could not retry", description: error?.message || "Please try again.", variant: "destructive" });
    },
  });
  const discardStarterMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/personas/${persona.id}/starter-discard`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/personas`] });
      toast({ title: "Starter work discarded", description: "Successful sibling personas were left unchanged." });
    },
    onError: (error: any) => {
      toast({ title: "Could not discard", description: error?.message || "Please try again.", variant: "destructive" });
    },
  });
  const pct = personaStrength(persona);
  const complete = isPersonaStrengthReady(pct);
  const isActive = persona.status === "active";
  const starterPaused = starterWork?.state === "interrupted" || starterWork?.state === "failed";
  const starterInProgress = starterWork?.state === "queued" || starterWork?.state === "running";
  const isFailed = persona.status === "failed" && !starterPaused;
  // A persona stays in the generating treatment until the whole build (5
  // characters + research) resolves — not just until the profile text lands.
  const isBuilding = ((persona as any).buildStatus === "building" || starterInProgress || starterPaused) && !isFailed;
  const isGenerating = persona.status === "generating" || isBuilding;
  const coverRegenerating = isPersonaCoverRegenerating(persona);
  const coverRegenerated = isPersonaCoverRegenerated(persona);
  const bp = (persona as any).buildProgress as BuildProgress | undefined;

  // One-time reveal: when the build this mounted card was watching resolves
  // successfully, the finished persona card animates in. Personas that mount
  // already-done never get the animation (the ref starts false for them).
  const wasBuildingRef = useRef(isGenerating);
  const [reveal, setReveal] = useState(false);
  useEffect(() => {
    if (wasBuildingRef.current && !isGenerating && !isFailed) {
      wasBuildingRef.current = false;
      setReveal(true);
      const t = window.setTimeout(() => setReveal(false), 900);
      return () => window.clearTimeout(t);
    }
    wasBuildingRef.current = isGenerating;
  }, [isGenerating, isFailed]);

  const heroReady = !coverRegenerating && !coverRegenerated && !!persona.heroImageUrl;
  const heroPending =
    !coverRegenerated &&
    !heroReady &&
    !!persona.imageUrl?.startsWith("http") &&
    persona.heroImageStatus !== "failed";
  // A regenerated cover is authoritative. Do not resume a stale landscape
  // derived from the old cover after the loading treatment settles.
  const bandImage = resolvePersonaCardBandImage(persona);

  const awarenessLabel = AWARENESS_STAGES.find(s => s.value === persona.awarenessStage)?.label;
  const metaParts = [
    persona.avatarName,
    awarenessLabel,
    campaignCount > 0 ? `${campaignCount} campaign${campaignCount > 1 ? "s" : ""}` : null,
  ].filter(Boolean) as string[];

  // Chips prefer the system-generated condensed phrasing (deepFearShort etc.,
  // filled shortly after profile creation / by the background backfill) and
  // fall back to line-clamping the full text until it lands. The detail page
  // always shows the full, unshortened text.
  const fear = persona.deepFearShort?.trim() || persona.deepFear?.trim() || null;
  const desire = persona.desiredOutcomeShort?.trim() || persona.desiredOutcome?.trim() || null;
  const trigger = persona.triggerShort?.trim()
    || firstText(persona.trustTriggersJson)
    || firstText(persona.emotionalHooksJson);
  // The quote strip prefers the Sim-provided starter-generator quote and
  // falls back to the first customer-voice quote for older personas.
  const quote = persona.cardQuote?.trim() || firstText(persona.customerVoiceQuotesJson);
  // Faint line-art sketch between the quote and the CORE TENSION block —
  // renders only once the one-shot generation has landed (missing/failed
  // sketches simply collapse the slot, no broken image or empty gap).
  const sketch = persona.sketchImageUrl || null;
  const tension = persona.currentFrustration?.trim() || persona.primaryPain?.trim() || persona.worldview?.trim() || null;

  if (isFailed) {
    // Failed builds keep the plain failed treatment with the delete affordance.
    const card = (
      <Card
        className={`persona-card relative overflow-hidden rounded-2xl h-full ${CARD_MIN_H} border-destructive/30`}
        style={{ animationDelay: `${index * 80}ms` }}
        data-testid={`card-persona-failed-${persona.id}`}
      >
        <CardContent className="p-0 flex flex-col h-full">
          <div className="relative overflow-hidden h-[200px] md:h-[210px] flex-shrink-0 flex flex-col justify-center px-5 py-5" style={{ backgroundColor: BAND }}>
            <div className="absolute inset-y-0 right-0 w-[45%] bg-gradient-to-l from-destructive/15 to-transparent flex items-center justify-center pointer-events-none">
              <span className="text-4xl font-bold text-white/10 tracking-wide">{getInitials(persona.name)}</span>
            </div>
            <div className="relative z-10 max-w-[60%] flex flex-col gap-2.5 overflow-hidden">
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-[0.18em] text-white/70">
                <Users className="h-3 w-3" />Persona
              </span>
              {/* Full name always shown — type steps down for long names. */}
              <h3 className={`${PERSONA_NAME_TIER_COMPACT[personaNameTier(persona.name)]} text-[#f3ede3]`} style={SERIF} data-testid={`text-name-${persona.id}`}>
                {persona.name}
              </h3>
              <p className="text-[12px] leading-[18px] flex items-center gap-1.5 text-red-300/80">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />Generation failed
              </p>
              {persona.summary && (
                <p className="text-[13px] text-white/45 leading-[19px] h-[38px] line-clamp-2 italic">{persona.summary}</p>
              )}
            </div>
          </div>
          <div className="mt-auto flex items-center justify-between gap-2 px-4 py-3">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 rounded-lg px-2.5 text-[11px] text-destructive hover:text-destructive hover:bg-destructive/10"
              disabled={deleteMutation.isPending}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(true); }}
              data-testid={`button-delete-persona-${persona.id}`}
            >
              {deleteMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Delete
            </Button>
            <Badge className="text-[9px] font-semibold uppercase tracking-widest rounded-full px-2.5 h-5 border bg-destructive/10 text-destructive border-destructive/20">
              Failed
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
    return (
      <>
        {card}
        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this failed persona?</AlertDialogTitle>
              <AlertDialogDescription>
                "{persona.name}" failed to generate and will be permanently removed. Saved narratives and campaign history will be kept. This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-delete-persona">Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (!deletingRef.current) {
                    deletingRef.current = true;
                    deleteMutation.mutate();
                  }
                }}
                data-testid="button-confirm-delete-persona"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  if (isGenerating) {
    // Dedicated narrated build card — never shows finished-card content
    // (portrait, summary, chips, quote) until the whole build resolves.
    // Once the profile has landed the persona detail page is useful (it shows
    // per-step progress), so the still-building card becomes clickable.
    const clickable = persona.status !== "generating";
    const card = (
      <PersonaBuildCard
        persona={{ id: persona.id, name: persona.name }}
        index={index}
        progress={bp ?? fallbackBuildProgress()}
        clickable={clickable}
        starterState={starterWork?.state === "queued" || starterWork?.state === "running"
          || starterWork?.state === "interrupted" || starterWork?.state === "failed"
          ? starterWork.state
          : undefined}
        starterError={starterWork?.error}
        onRetry={() => retryStarterMutation.mutate()}
        onDiscard={() => discardStarterMutation.mutate()}
        recoveryPending={retryStarterMutation.isPending || discardStarterMutation.isPending}
      />
    );
    return clickable
      ? <Link href={`/brands/${brandId}/personas/${persona.id}`}>{card}</Link>
      : card;
  }

  return (
    <Link href={`/brands/${brandId}/personas/${persona.id}`}>
      <Card
        className={`persona-card group relative cursor-pointer h-full ${CARD_MIN_H} overflow-hidden rounded-2xl border-border/40 hover:border-border/80 hover:shadow-lg transition-all duration-200 ${reveal ? "persona-reveal" : ""}`}
        style={{ animationDelay: `${index * 80}ms` }}
        data-testid={`card-persona-${persona.id}`}
      >
        <CardContent className="p-0 flex flex-col h-full">
          {/* ── Dark cinematic hero band ─────────────────────────────── */}
          <div className="relative overflow-hidden h-[200px] md:h-[210px] flex-shrink-0 flex" style={{ backgroundColor: BAND }}>
            {coverRegenerating ? (
              <div
                className="absolute inset-0 flex items-center justify-center"
                role="status"
                aria-live="polite"
                aria-label="Regenerating cover photo"
                data-testid={`cover-regenerating-${persona.id}`}
              >
                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-white/75">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  Regenerating cover
                </span>
              </div>
            ) : bandImage ? (
              <div
                className="absolute inset-0 md:inset-y-0 md:left-auto md:right-0 md:w-[58%]"
                data-testid={`img-avatar-${persona.id}`}
              >
                <img
                  src={optimizedImg(bandImage, 1280)}
                  alt={persona.name}
                  className={`w-full h-full object-cover ${heroReady ? "object-center" : "object-top"} transition-transform duration-500 group-hover:scale-[1.02]`}
                  loading="lazy"
                />
                {/* Blend into the dark band. Overlay extends a few px past the
                    image's left edge so subpixel rounding at fractional
                    percentage widths can never leave an uncovered hairline. */}
                <div
                  className="absolute inset-y-0 -left-1 right-0 pointer-events-none"
                  style={{ background: `linear-gradient(to right, ${BAND} 0%, ${BAND}cc 18%, ${BAND}33 55%, transparent 100%)` }}
                />
                <div
                  className="absolute inset-x-0 bottom-0 h-16 pointer-events-none md:hidden"
                  style={{ background: `linear-gradient(to top, ${BAND}e6, transparent)` }}
                />
              </div>
            ) : (
              <div className="absolute inset-y-0 right-0 w-[50%] flex items-center justify-center bg-gradient-to-l from-white/[0.07] via-white/[0.03] to-transparent pointer-events-none">
                <span className="text-6xl font-bold text-white/10 tracking-wide">{getInitials(persona.name)}</span>
              </div>
            )}

            {heroPending && (
              <span className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 px-2.5 py-1 text-[9px] font-medium uppercase tracking-wider text-white/70 pointer-events-none" data-testid={`hero-pending-${persona.id}`}>
                <Loader2 className="h-3 w-3 animate-spin" />Creating scene
              </span>
            )}

            {/* The band itself is fixed-height so the grid stays uniform, but
                the name slot is flexible: names are never truncated — long
                names step down in type size (personaNameTier) and wrap to more
                lines instead. Meta stays one line and summary two, so the band
                never grows; the column centers vertically within the band. */}
            <div className="relative z-10 flex flex-col justify-center gap-2 p-5 w-full md:max-w-[52%] overflow-hidden">
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-[0.18em] text-white/75 backdrop-blur-sm">
                <Users className="h-3 w-3" />Persona
              </span>
              <h3
                className={`text-[#f5efe4] tracking-tight ${MAIN_NAME_TIER_CLASS[personaNameTier(persona.name)]}`}
                style={SERIF}
                data-testid={`text-name-${persona.id}`}
              >
                {persona.name}
              </h3>
              <p className="text-[11.5px] leading-[16px] h-[16px] truncate text-white/75 font-medium" data-testid={`text-meta-${persona.id}`}>
                {metaParts.map((part, i) => (
                  <span key={i}>
                    {i > 0 && <span className="mx-1.5 text-white/40">•</span>}
                    {part}
                  </span>
                ))}
              </p>
              <p className="text-[12px] text-white/55 leading-[17px] h-[34px] line-clamp-2 mt-0.5" data-testid={`text-summary-${persona.id}`}>
                {persona.summary || "No summary yet — click to add persona details"}
              </p>
            </div>
          </div>

          {/* ── Fear / Desire / Trigger chips ────────────────────────── */}
          {/* Three equal-width chips in one horizontal row. All three slots
              always render (quiet placeholder when a persona is missing one)
              so the chip band is the same height on every card. */}
          <div className="grid grid-cols-3 gap-2 px-4 pt-4">
            <TraitChip
              label="Fear" text={fear} icon={Shield} testId={`chip-fear-${persona.id}`}
              tint={{ icon: "text-rose-500", iconBg: "bg-rose-500/10 border-rose-500/15", label: "text-rose-500/90" }}
            />
            <TraitChip
              label="Desire" text={desire} icon={Heart} testId={`chip-desire-${persona.id}`}
              tint={{ icon: "text-orange-500", iconBg: "bg-orange-500/10 border-orange-500/15", label: "text-orange-500/90" }}
            />
            <TraitChip
              label="Trigger" text={trigger} icon={Zap} testId={`chip-trigger-${persona.id}`}
              tint={{ icon: "text-emerald-500", iconBg: "bg-emerald-500/10 border-emerald-500/15", label: "text-emerald-500/90" }}
            />
          </div>

          {/* ── Quote + core tension strip ───────────────────────────── */}
          {/* Matches the reference card: oversized quote mark + italic serif
              quote on the left, a faint hand-drawn line-art brand sketch in
              the middle, and the CORE TENSION block + chevron on the right.
              Collapsed, both text blocks reserve exactly three lines so the
              strip is the same height on every card; the sketch slot only
              renders when the illustration exists (the text blocks flex wider
              in its absence — no broken image, no empty gap). Expanding only
              grows this card. */}
          <div className="mx-4 mt-3 rounded-xl bg-amber-500/[0.05] border border-amber-500/10 px-4 py-3.5 flex items-center gap-3">
            <div className="flex-[1.2] min-w-0 flex gap-2 self-center">
              <span className="text-[38px] leading-[30px] text-emerald-800/75 select-none flex-shrink-0" style={SERIF}>&ldquo;</span>
              <p
                className={`italic text-[13.5px] leading-[18px] self-center ${tensionOpen ? "min-h-[54px]" : "line-clamp-3 max-h-[54px]"} ${quote ? "text-foreground/85" : "text-muted-foreground/45"}`}
                style={SERIF}
                data-testid={`text-quote-${persona.id}`}
              >
                {quote || "No customer quote captured yet"}
              </p>
            </div>
            {sketch && (
              /* The slot stretches to the strip's text-driven height and the
                 (larger) artwork is absolutely centered inside it, so the
                 sketch never changes the strip height — cards with and
                 without a sketch stay identical. Full-strength multiply (no
                 partial opacity) makes the cream PNG background vanish into
                 the tan strip, and a radial mask fades the artwork's edges so
                 nothing terminates in a hard rectangle (same in dark mode,
                 where invert+screen does the blending). */
              <div className="relative hidden sm:block w-[120px] flex-shrink-0 self-stretch" data-testid={`img-sketch-${persona.id}`}>
                <img
                  src={optimizedImg(sketch, 480)}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-[92px] w-full object-contain mix-blend-multiply dark:invert dark:mix-blend-screen dark:opacity-70 pointer-events-none select-none"
                  style={{
                    WebkitMaskImage: "radial-gradient(ellipse farthest-side at center, black 55%, transparent 100%)",
                    maskImage: "radial-gradient(ellipse farthest-side at center, black 55%, transparent 100%)",
                  }}
                />
              </div>
            )}
            <div className="flex-1 min-w-0 flex items-start gap-2 border-l border-amber-500/10 pl-3">
              <div className="flex-1 min-w-0">
                <p className="text-[9.5px] leading-[13px] font-bold uppercase tracking-[0.15em] text-amber-600/90 mb-1">Core Tension</p>
                <p
                  className={`text-[11px] leading-[16px] ${tensionOpen ? "min-h-[48px]" : "line-clamp-3 h-[48px]"} ${tension ? "text-muted-foreground" : "text-muted-foreground/45 italic"}`}
                  data-testid={`text-tension-${persona.id}`}
                >
                  {tension || "No core tension captured yet"}
                </p>
              </div>
              {tension && (
                <button
                  className="w-7 h-7 rounded-full border border-border/60 bg-card flex items-center justify-center flex-shrink-0 hover:bg-muted/50 transition-colors self-center"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTensionOpen(o => !o); }}
                  data-testid={`button-expand-tension-${persona.id}`}
                  aria-label={tensionOpen ? "Collapse" : "Expand"}
                >
                  <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${tensionOpen ? "rotate-180" : ""}`} />
                </button>
              )}
            </div>
          </div>

          {/* ── Footer ───────────────────────────────────────────────── */}
          <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-3 px-4 py-3.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <StrengthGauge pct={pct} />
              <div className="min-w-0">
                <p className="text-[12px] font-semibold leading-tight whitespace-nowrap">Persona Strength</p>
                <p className="text-[10.5px] text-muted-foreground leading-tight mt-0.5 truncate">
                  {complete ? "Rich insights. Ready to activate." : "Add more detail to strengthen."}
                </p>
                {/* Slot is always reserved so the footer is the same height
                    whether or not the persona is complete. */}
                <div className="h-5 mt-1">
                  {complete && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/15 px-2 py-0.5 text-[9px] font-semibold text-emerald-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Persona Ready
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <Button
                className="h-9 rounded-lg px-3.5 gap-1.5 text-[13px] bg-emerald-700 hover:bg-emerald-600 text-white shadow-sm"
                data-testid={`button-open-persona-${persona.id}`}
              >
                <SquareArrowOutUpRight className="h-3.5 w-3.5" />Open
              </Button>
              <div className="flex items-center gap-1.5" onClick={e => e.preventDefault()}>
                <ShareDialog
                  resourceType="persona"
                  resourceId={persona.id}
                  resourceName={persona.name}
                  workspaceId={workspaceId}
                  iconOnly
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full w-8 h-8"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBookmarked(b => !b); }}
                  data-testid={`button-bookmark-${persona.id}`}
                  aria-label="Bookmark persona"
                >
                  <Bookmark className={`h-4 w-4 ${bookmarked ? "fill-current text-emerald-500" : "text-muted-foreground"}`} />
                </Button>
              </div>
              <Badge
                className={`text-[9px] font-semibold uppercase tracking-widest rounded-full px-3 h-6 border inline-flex items-center gap-1.5 ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                    : "bg-amber-500/10 text-amber-500 border-amber-500/20"
                }`}
                data-testid={`badge-status-${persona.id}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : "bg-amber-500"}`} />
                {persona.status}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function PersonasPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [awarenessFilter, setAwarenessFilter] = useState("all");
  const [sortBy, setSortBy] = useState("strength");

  const { data: brand } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    enabled: !!brandId,
  });

  const { data: personas, isLoading } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
    refetchInterval: (query) => {
      const list = query.state.data;
      if (list && list.some(p =>
        isPersonaCoverRegenerating(p) ||
        !p.imageUrl ||
        p.status === "generating" ||
        // Keep polling while any persona's aggregate build (characters +
        // research rollup) is unresolved, not just while profile text runs.
        (p as any).buildStatus === "building" ||
        // Landscape hero still generating (only for fetchable http profile
        // photos — local-path legacy images never get a hero, don't poll).
        (!p.heroImageUrl && p.heroImageStatus !== "failed" && !!p.imageUrl?.startsWith("http")) ||
        // Quote-strip sketch claimed and in flight — poll until it lands.
        (!p.sketchImageUrl && p.sketchImageStatus === "generating")
      )) return 4000;
      return false;
    },
  });

  const { data: campaigns } = useQuery<Campaign[]>({
    queryKey: [`/api/brands/${brandId}/campaigns`],
    enabled: !!brandId,
  });

  const campaignCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    campaigns?.forEach(c => {
      if (c.personaId) counts[c.personaId] = (counts[c.personaId] || 0) + 1;
    });
    return counts;
  }, [campaigns]);

  const filteredPersonas = useMemo(() => {
    let result = personas || [];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.summary || "").toLowerCase().includes(q) ||
        (p.worldview || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") {
      result = result.filter(p => p.status === statusFilter);
    }
    if (awarenessFilter !== "all") {
      result = result.filter(p => p.awarenessStage === awarenessFilter);
    }
    result = [...result].sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "created") return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      if (sortBy === "strength") {
        const diff = personaStrength(b) - personaStrength(a);
        if (diff !== 0) return diff;
        // Tie-break so equally-strong personas keep a stable, recent-first order.
        return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
      }
      return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
    });
    return result;
  }, [personas, search, statusFilter, awarenessFilter, sortBy]);

  const activeCount = personas?.filter(p => p.status === "active").length || 0;
  const completeCount = personas?.filter(p => isPersonaStrengthReady(personaStrength(p))).length || 0;
  const totalCampaigns = Object.values(campaignCounts).reduce((a, b) => a + b, 0);
  const stageBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    personas?.forEach(p => {
      if (p.awarenessStage) counts[p.awarenessStage] = (counts[p.awarenessStage] || 0) + 1;
    });
    return counts;
  }, [personas]);

  if (isLoading) {
    return (
      <div className="p-8 max-w-6xl mx-auto space-y-6">
        <Skeleton className="h-32 rounded-2xl" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map(i => <Skeleton key={i} className="h-[480px] rounded-2xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/10 px-8 py-8 fade-in-up">
        <div className="absolute top-0 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-1/3 w-48 h-48 bg-violet-500/5 rounded-full blur-3xl translate-y-1/2" />
        <div className="relative flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center border border-primary/20">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight" data-testid="text-personas-title">Personas</h1>
                <p className="text-sm text-muted-foreground">Persuasion profiles for campaign targeting</p>
              </div>
            </div>
          </div>
          <Link href={`/brands/${brandId}/personas/new`}>
            <Button className="rounded-full gap-2 px-5 shadow-lg shadow-primary/20" data-testid="button-create-persona">
              <Plus className="h-4 w-4" />New Persona
            </Button>
          </Link>
        </div>
      </div>

      {personas && personas.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 fade-in-up-delay-1">
          <StatPill label="Total Personas" value={personas.length} icon={Users} />
          <StatPill label="Active" value={activeCount} icon={Activity} accent="bg-emerald-500/15" />
          <StatPill label="Complete" value={completeCount} icon={Shield} accent="bg-violet-500/15" />
          <StatPill label="Campaigns" value={totalCampaigns} icon={Zap} accent="bg-amber-500/15" />
        </div>
      )}

      {personas && personas.length > 0 && Object.keys(stageBreakdown).length > 1 && (
        <div className="flex items-center gap-1.5 fade-in-up-delay-1">
          {AWARENESS_STAGES.map(stage => {
            const count = stageBreakdown[stage.value] || 0;
            if (count === 0) return null;
            const colors = AWARENESS_COLORS[stage.value];
            return (
              <div
                key={stage.value}
                className={`flex-1 h-2 rounded-full ${colors.bg} relative overflow-hidden`}
                style={{ flex: count }}
                title={`${stage.label}: ${count}`}
              >
                <div className={`absolute inset-0 ${colors.dot} opacity-40 rounded-full`} />
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 fade-in-up-delay-1">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search personas by name, summary, or worldview..."
            className="pl-9 h-10 bg-card/50"
            data-testid="input-search-personas"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px] bg-card/50" data-testid="select-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
          </SelectContent>
        </Select>
        <Select value={awarenessFilter} onValueChange={setAwarenessFilter}>
          <SelectTrigger className="w-[160px] bg-card/50" data-testid="select-filter-awareness">
            <SelectValue placeholder="Awareness" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {AWARENESS_STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[150px] bg-card/50" data-testid="select-sort">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="strength">Strength</SelectItem>
            <SelectItem value="updated">Last Updated</SelectItem>
            <SelectItem value="created">Created</SelectItem>
            <SelectItem value="name">Name</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredPersonas.length === 0 ? (
        <div className="text-center py-20 fade-in-up-delay-2">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
            <Users className="h-8 w-8 text-primary/50" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1.5">
            {personas?.length === 0 ? "No personas yet" : "No matching personas"}
          </h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
            {personas?.length === 0 ? "Create your first persuasion profile to start building targeted campaigns" : "Try adjusting your filters or search terms"}
          </p>
          {personas?.length === 0 && (
            <Link href={`/brands/${brandId}/personas/new`}>
              <Button className="rounded-full px-6" data-testid="button-create-first-persona">
                <Plus className="h-4 w-4 mr-1.5" />Create Persona
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 fade-in-up-delay-2">
          {filteredPersonas.map((persona, i) => (
            <PersonaCard
              key={persona.id}
              persona={persona}
              brandId={brandId!}
              campaignCount={campaignCounts[persona.id] || 0}
              index={i}
              workspaceId={brand?.workspaceId || ""}
            />
          ))}
        </div>
      )}
    </div>
  );
}
